import { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';
import { ErrorData } from '../../../../types';
import useSnackBar from '../../../../hooks/useSnackBar';
import { setClientExternalVendorId, removeClientExternalVendorId } from '../../../../api/clientsApi/clientsApi';
import ConfirmationPopup from '../../../../components/ConfirmationPopup';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Theme,
  Button,
  Tooltip,
  useTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import LaunchIcon from '@mui/icons-material/Launch';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { NETSUITE_LABEL } from '../../../../constants/constants';
import TruncatedText from '../../../../components/TruncatedText';
import FindNetSuiteVendorPopup from '../../../../components/FindNetSuiteVendorPopup';
import { numberToUSDCurrency } from '../../../../helpers/numberToUSDCurrency';
import { accountingVendorType } from '../../../VendorsView/helpers';

const useStyles = makeStyles()((theme: Theme) => ({
  accordion: {
    backgroundColor: 'transparent',
    marginTop: theme.spacing(1),
    boxShadow: 'none',
    overflow: 'hidden',
    fontFamily: 'Inter',
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    minHeight: '56px',
    padding: '0 16px',
    fontFamily: 'Inter',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '16px',
      color: theme.palette.common.white,
      fontFamily: 'Inter',
    },
    flexDirection: 'row-reverse',
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.light,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    fontFamily: 'Inter',
  },
  plaintiffRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: 'transparent',
  },
  plaintiffInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  plaintiffName: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.common.white,
  },
  plaintiffHelperText: {
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 400,
    color: '#FFFFFFB3',
  },
  tableContainer: {
    background: 'transparent',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    overflow: 'auto',
    fontSize: '14px',
    lineHeight: '17px',
    fontWeight: 500,
    fontFamily: 'Inter',
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  tableHeaderCell: {
    background: theme.palette.primary.light,
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.text.secondary,
      borderBottom: '1px solid #515151',
      fontWeight: 500,
      fontFamily: 'Inter',
      fontSize: '14px',
      height: '35px',
      padding: '16px 12px 16px 8px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  tableBodyCell: {
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.common.white,
      borderBottom: '1px solid #4A4E5D',
      fontWeight: 400,
      fontFamily: 'Inter',
      fontSize: '14px',
      padding: '16px 12px 16px 8px',
      minWidth: '140px',
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      wordWrap: 'break-word',
      wordBreak: 'break-word',
    },
  },
  columnName: {
    minWidth: 320,
    maxWidth: 320,
  },
  columnAmount: {
    minWidth: 210,
    maxWidth: 210,
  },

  columnAction: {
    minWidth: 100,
    maxWidth: 100,
  },
  columnActionWide: {
    minWidth: 180,
    maxWidth: 180,
  },
}));

interface NetSettlementData {
  name?: string;
  amount?: number;
  clientId?: number;
  accounting?: {
    isSynced?: boolean;
    link?: string | null;
  };
}

interface DraftPlaintiffTableProps {
  netSettlement?: NetSettlementData;
  clientId?: number;
  onUpdate?: (updatedData: NetSettlementData) => void;
}

const DraftPlaintiffTable = ({ netSettlement, clientId, onUpdate }: DraftPlaintiffTableProps) => {
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();
  const theme = useTheme();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [syncAction, setSyncAction] = useState<'sync' | 'relink'>('sync');
  const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);
  const [isSynced, setIsSynced] = useState<boolean>(netSettlement?.accounting?.isSynced ?? false);
  const [netSuiteLink, setNetSuiteLink] = useState<string | null>(netSettlement?.accounting?.link ?? null);

  const vendorType = accountingVendorType;
  const effectiveClientId = clientId || netSettlement?.clientId;
  const plaintiffName = netSettlement?.name || '';

  const handleSyncButtonClick = () => {
    setSyncAction('sync');
    setIsPopupOpen(true);
  };

  const handleRelinkButtonClick = () => {
    setSyncAction('relink');
    setIsPopupOpen(true);
  };

  // Sync/relink directly from the review popup, no confirmation step
  const handleNetSuiteVendorSelect = async (internalId: string) => {
    if (!effectiveClientId || !internalId) return;
    try {
      const response = await setClientExternalVendorId(effectiveClientId, internalId);
      setAlert(() => ({
        message: `Plaintiff ${syncAction === 'sync' ? 'synced' : 'relinked'} successfully.`,
        type: 'success',
      }));
      const newIsSynced = response.data.isSynced ?? false;
      const newLink = response.data.link ?? null;
      setIsPopupOpen(false);
      setIsSynced(newIsSynced);
      setNetSuiteLink(newLink);

      // Notify parent of update
      if (onUpdate && netSettlement) {
        onUpdate({
          ...netSettlement,
          accounting: {
            ...netSettlement.accounting,
            isSynced: newIsSynced,
            link: newLink,
          },
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert(() => ({
        message: axiosError.response?.data?.message || `Failed to ${syncAction} plaintiff.`,
        type: 'error',
      }));
    }
  };

  const handleConfirmUnlink = async () => {
    if (!effectiveClientId) return;
    try {
      const response = await removeClientExternalVendorId(effectiveClientId);
      setAlert(() => ({
        message: response.data.message || 'Plaintiff unlinked successfully.',
        type: 'success',
      }));
      setIsUnlinkConfirmOpen(false);
      setIsSynced(false);
      setNetSuiteLink(null);

      // Notify parent of update
      if (onUpdate && netSettlement) {
        onUpdate({
          ...netSettlement,
          accounting: {
            ...netSettlement.accounting,
            isSynced: false,
            link: null,
          },
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert(() => ({
        message: axiosError.response?.data?.message || 'Failed to unlink plaintiff.',
        type: 'error',
      }));
    }
  };

  return (
    <>
      <Accordion className={classes.accordion} defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
          className={classes.accordionSummary}
        >
          <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
            <Typography>Plaintiff</Typography>
            {netSettlement?.accounting && !isSynced && <Typography className={classes.plaintiffHelperText} sx={{ fontWeight: '400 !important', fontSize: '12px !important', color: '#FFFFFFB3 !important' }}>Required for {vendorType} transaction entries</Typography>}
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <TableContainer className={classes.tableContainer} sx={{ width: '99%' }} component={Paper} elevation={0}>
            <Table sx={{ minWidth: '430px', borderCollapse: 'collapse' }}>
              <TableHead className={classes.tableHeaderCell}>
                <TableRow>
                  <TableCell className={classes.columnName}>{'Name'}</TableCell>
                  <TableCell className={classes.columnAmount}>{'Amount'}</TableCell>
                  {netSettlement?.accounting && <>
                    <TableCell className={classes.columnAction} />
                    <TableCell className={classes.columnActionWide}>{'Action'}</TableCell>
                  </>}
                </TableRow>
              </TableHead>
              <TableBody className={classes.tableBodyCell}>
                <TableRow>
                  <TableCell className={classes.columnName}>
                    <Box display="flex" alignItems="center" gap="8px" overflow="hidden">
                      <TruncatedText text={plaintiffName || '-'} maxLength={30} />
                      {netSuiteLink && (
                        <Tooltip
                          title={`View Plaintiff in ${NETSUITE_LABEL}`}
                          componentsProps={{
                            tooltip: {
                              sx: (theme) => ({
                                backgroundColor: theme.palette.common.white,
                                color: theme.palette.primary.light,
                                borderRadius: '5px',
                                boxShadow: '3',
                                fontFamily: 'Inter',
                                fontSize: '13px',
                                fontWeight: 500,
                                padding: '5px',
                                maxWidth: '140px',
                              }),
                            },
                          }}
                        >
                          <LaunchIcon
                            sx={{
                              cursor: 'pointer',
                              color: 'common.white',
                              fontSize: '20px',
                              flexShrink: 0,
                            }}
                            onClick={() => window.open(netSuiteLink, '_blank')}
                          />
                        </Tooltip>
                      )}
                      {netSettlement?.accounting &&
                        <Box
                          sx={{
                            backgroundColor: isSynced ? 'success.main' : 'warning.main',
                            color: 'white',
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '8px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isSynced ? 'Synced' : 'Unsynced'}
                        </Box>}
                    </Box>
                    {netSettlement?.accounting && !isSynced && <Typography className={classes.plaintiffHelperText} sx={{ paddingTop: '4px' }}>
                      Plaintiff entity must exist in {vendorType} before posting transaction entries.
                    </Typography>}
                  </TableCell>
                  <TableCell className={classes.columnAmount}>{numberToUSDCurrency(netSettlement?.amount) || '-'}</TableCell>

                  {netSettlement?.accounting && (
                    <TableCell className={classes.columnAction} />
                  )}
                  {netSettlement?.accounting && (
                    <TableCell className={classes.columnActionWide}>
                      <Box
                        display="flex"
                        gap="8px"
                        flexWrap="nowrap"
                        alignItems="center"
                      >
                        {isSynced ? (
                          <>
                            <Button
                              variant="contained"
                              startIcon={<LinkIcon />}
                              onClick={handleRelinkButtonClick}
                              sx={{
                                backgroundColor: theme.palette.info.main,
                                color: 'white',
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 500,
                                padding: '6px 10px',
                                borderRadius: '12px',
                                whiteSpace: 'nowrap',
                                '&:hover': { backgroundColor: theme.palette.info.dark },
                              }}
                            >
                              Relink
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<LinkOffIcon />}
                              onClick={() => setIsUnlinkConfirmOpen(true)}
                              sx={{
                                borderColor: theme.palette.error.main,
                                color: theme.palette.error.main,
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 500,
                                padding: '6px 10px',
                                borderRadius: '12px',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                  borderColor: theme.palette.error.dark,
                                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                },
                              }}
                            >
                              Unlink
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="contained"
                            startIcon={<SearchIcon sx={{ fontSize: '18px' }} />}
                            onClick={handleSyncButtonClick}
                            sx={{
                              backgroundColor: theme.palette.info.main,
                              color: 'white',
                              textTransform: 'none',
                              fontSize: '14px',
                              fontWeight: 500,
                              padding: '6px 10px',
                              borderRadius: '12px',
                              width: '182px',
                              whiteSpace: 'nowrap',
                              '&:hover': { backgroundColor: theme.palette.info.dark },
                            }}
                          >
                            Match plaintiff
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <FindNetSuiteVendorPopup
        open={isPopupOpen}
        lienName={plaintiffName}
        searchTerm={plaintiffName}
        entityType="Plaintiff"
        entityLabel="Plaintiff"
        entityId={effectiveClientId}
        onClose={() => setIsPopupOpen(false)}
        onVendorSelectFromNetsuite={handleNetSuiteVendorSelect}
        syncAction={syncAction}
      />

      <ConfirmationPopup
        open={isUnlinkConfirmOpen}
        title="Confirm Unlink"
        message={`Are you sure you want to unlink this Plaintiff ${plaintiffName} from ${vendorType}? This action will remove the connection between V.Law and ${vendorType} for this plaintiff.`}
        confirmText="Unlink"
        onConfirm={handleConfirmUnlink}
        onCancel={() => setIsUnlinkConfirmOpen(false)}
      />
    </>
  );
};

export default DraftPlaintiffTable;
